import Skeleton from '@mui/material/Skeleton'
import classNames from 'classnames'
import _ from 'lodash'
import React, { useEffect, useState } from 'react'
import { useDeepCompareEffect } from 'react-use'
import * as styles from './Photo.module.scss'
import { PhotoInterface, PhotoMediaTypeEnum, PhotoSetInterface, PhotosInterface } from '../../interfaces/PhotoInterface'
import isPrerender from '../../utils/isPrerender'
import Video from '../Video/Video'

export const IMAGE_NOT_FOUND = '/img/not_found.jpg'
type PhotoSetType = 'product' | 'catalog' | 'thumb' | 'custom' | 'landing'
type PhotoTypeType = 'product' | 'catalog'

type DefaultProps = {
  set: PhotoSetType
  type: PhotoTypeType
}

type Props = Partial<DefaultProps> & {
  link?: string
  alt?: string
  classes?: string
  src: string | PhotosInterface | PhotoSetInterface
  secondSrc?: string | PhotosInterface | PhotoSetInterface
  width?: number
  height?: number
  noEmpty?: true
  minHeight?: number
  noVideoControlls?: boolean
  onClick?: () => void
}

const defaultProps: DefaultProps = {
  set: 'product',
  type: 'product',
}

function sortPosition(imageA: PhotoInterface, imageB: PhotoInterface) {
  return imageA.position - imageB.position
}

type SourceResultType = {
  path: string
  mediaType: PhotoMediaTypeEnum
}

const getSource = (
  src: string | PhotosInterface | PhotoSetInterface,
  second: boolean,
  type: PhotoTypeType,
  set: PhotoSetType,
  width?: number,
  height?: number,
): SourceResultType | false => {
  let path = ''
  let mediaType = PhotoMediaTypeEnum.IMAGE

  const query = width! > 0 || height! > 0 ? '?' + (width! > 0 ? `w=${width}` : '') + (height! > 0 ? `w=${height}` : '') : ''
  if (typeof src === 'string') {
    path = `${src}${query}`
  } else if (set && _.has(src, set) && _.isArray(src[set])) {
    src = src as PhotosInterface
    const photosTyped = src[set].filter(item => item[type])

    let foundPhotoIdx = photosTyped.sort(sortPosition).findIndex(item => {
      return item[type] && item[type] === true
    })

    if (second) {
      foundPhotoIdx = photosTyped[foundPhotoIdx + 1] ? foundPhotoIdx + 1 : foundPhotoIdx
    }
    if (second && foundPhotoIdx === 0) {
      return false
    }
    path = `${src[set][foundPhotoIdx]?.url}${query}` || IMAGE_NOT_FOUND
    mediaType = src[set][foundPhotoIdx]?.mediaType || PhotoMediaTypeEnum.IMAGE
  } else if (set && _.has(src, set) && _.isObject(src[set])) {
    path = `${(src as PhotoSetInterface)[set]?.url}${query}` || IMAGE_NOT_FOUND
    mediaType = (src as PhotoSetInterface)[set]?.mediaType || PhotoMediaTypeEnum.IMAGE
  }

  return {
    path,
    mediaType,
  }
}

const isSourceEqual = (source: SourceResultType | false, secondSource: SourceResultType | false) => {
  if (source === false) {
    return false
  }

  if (typeof source === 'string' && typeof secondSource === 'string') {
    return source === secondSource
  }

  if (typeof source === 'object' && typeof secondSource === 'object') {
    return _.isEqual(source, secondSource)
  }
  return false
}

const Photo = (props: Props) => {
  const { classes, alt, width, height, src, noEmpty, type, secondSrc, set, minHeight } = { ...defaultProps, ...props }
  const [activeSec, setActiveSec] = React.useState<boolean>(false)
  const fixedSet = type === 'catalog' ? 'catalog' : set
  const [source, setSource] = useState<SourceResultType | false>(false)
  const [secondSource, setSecondSource] = useState<SourceResultType | false>(false)

  const [loaded, setLoaded] = useState<boolean>(false)
  const [secondLoaded, setSecondLoaded] = useState<boolean>(false)

  const skipSecond =
    (source && source.mediaType === PhotoMediaTypeEnum.VIDEO) || (secondSource && secondSource.mediaType === PhotoMediaTypeEnum.VIDEO)

  useDeepCompareEffect(() => {
    const _source = getSource(src, false, type, fixedSet, width, height)
    // if (!_source || !source) return
    //if (loaded && !isSourceEqual(_source, source)) {
    if (!isSourceEqual(_source, source)) {
      setLoaded(false)
      setSource(_source)
    }

    const _secondSource = secondSrc ? getSource(secondSrc, true, type, fixedSet, width, height) : false
    if (!_.isEqual(_secondSource, secondSource)) {
      setSecondLoaded(false)
      setSecondSource(secondSrc ? _secondSource : false)
    }
  }, [src, secondSrc, loaded, secondLoaded])

  const onMouseEnter = () => {
    if (skipSecond) return
    if (typeof secondSource === 'object') {
      setActiveSec(true)
    }
  }

  const onMouseOut = () => {
    setActiveSec(false)
  }

  const onImageError = () => {
    setSource(isPrerender() ? source : { path: IMAGE_NOT_FOUND, mediaType: PhotoMediaTypeEnum.IMAGE })
    setLoaded(true)
  }

  const onSecondImageError = () => {
    setSecondSource(isPrerender() ? secondSource : { path: IMAGE_NOT_FOUND, mediaType: PhotoMediaTypeEnum.IMAGE })
    setSecondLoaded(true)
  }

  const onClick = () => {
    if (props.onClick) {
      props.onClick()
    }
  }

  const firstImage =
    source && source.path ? (
      <img
        onClick={onClick}
        width="100%"
        height="100%"
        className={classNames(classes, styles['img'], {
          [styles['img--hidden']]: activeSec,
          [styles['img--abs']]: !!secondSource,
        })}
        style={{ minHeight }}
        src={source ? source.path : ''}
        alt={props.alt}
        onMouseEnter={onMouseEnter}
        onLoad={() => {
          setLoaded(true)
        }}
        onError={onImageError}
      />
    ) : null

  const secondImage =
    !skipSecond && secondSource && secondSource.path ? (
      <img
        onClick={onClick}
        width="100%"
        height="100%"
        className={classNames(classes, styles['img'], {
          [styles['img--hidden']]: !activeSec,
          [styles['img--abs']]: !!secondSource,
        })}
        style={{ minHeight }}
        src={secondSource ? secondSource.path : ''}
        alt={props.alt}
        onMouseEnter={onMouseEnter}
        onLoad={() => {
          setLoaded(true)
        }}
        onMouseLeave={onMouseOut}
        onError={onSecondImageError}
      />
    ) : null

  const imgsContent = (
    <>
      {firstImage}
      {secondImage}
    </>
  )
  const imgRender = props.secondSrc ? <div className={styles['rollover']}>{imgsContent}</div> : imgsContent
  return (
    <>
      {!loaded && !activeSec ? (
        <Skeleton
          classes={{
            root: classNames(classes, styles['img'], styles['img--skeleton']),
          }}
          variant="rectangular"
          animation="pulse"
        />
      ) : null}

      {source && source.mediaType === PhotoMediaTypeEnum.VIDEO ? (
        <Video
          sources={[{ src: source.path, type: 'video/mp4', withCredentials: true }]}
          options={{ cover: true, fill: false, responsive: true, controls: !props.noVideoControlls, fluid: true }}
          onReady={() => {
            setLoaded(true)
          }}
          linkHref={props.link}
          linkTarget="_self"
        />
      ) : null}

      {source && source.mediaType === PhotoMediaTypeEnum.IMAGE ? imgRender : null}
    </>
  )
}

export default Photo
